/**
 * Shared job workflow helpers for garage boards, modals, and technician dispatch.
 */

/** Quote / customer approval stages (card pills). Older keys kept for existing jobs. */
export const APPROVAL_STATUSES = {
  in_review: { label: 'In Review', color: '#F59E0B', order: 1 },
  quote_sent_to_customer: { label: 'Quote Sent to Customer', color: '#3B82F6', order: 2 },
  pending_manager_approval: { label: 'Pending Manager Approval', color: '#8B5CF6', order: 3 },
  pending_customer_approval: { label: 'Pending Customer Approval', color: '#6366F1', order: 4 },
  job_authorised: { label: 'Job Authorised', color: '#10B981', order: 5 }
};

/** Stages shown on quote workflow pills (no manager-approval gate). */
export const QUOTE_WORKFLOW_STAGES = ['in_review', 'quote_sent_to_customer', 'job_authorised'];

/** Timeline event labels — includes how the quote was given (email, WhatsApp, phone, in person). */
export const APPROVAL_HISTORY_EVENTS = {
  in_review: { label: 'Estimate saved', color: '#F59E0B' },
  quote_sent_to_customer: { label: 'Quote sent to customer', color: '#3B82F6' },
  quote_sent_email: { label: 'Quote emailed', color: '#3B82F6' },
  quote_sent_whatsapp: { label: 'Quote sent on WhatsApp', color: '#128C7E' },
  quote_given_phone: { label: 'Quote given by phone', color: '#0EA5E9' },
  quote_given_in_person: { label: 'Quote given in person', color: '#8B5CF6' },
  pending_manager_approval: { label: 'Pending manager approval', color: '#8B5CF6' },
  pending_customer_approval: { label: 'Pending customer approval', color: '#6366F1' },
  job_authorised: { label: 'Job authorised', color: '#10B981' },
  rejected: { label: 'Rejected', color: '#EF4444' },
  pre_work_submitted: { label: 'Pre-work submitted', color: '#F59E0B' },
  pre_work_approved: { label: 'Pre-work approved', color: '#10B981' },
  pre_work_rejected: { label: 'Pre-work sent back', color: '#EF4444' },
  inspection_emailed: { label: 'Inspection emailed', color: '#3B82F6' },
  inspection_video_uploaded: { label: 'Inspection video saved', color: '#64748B' },
  final_video_uploaded: { label: 'Completion video saved', color: '#64748B' },
  technician_assigned: { label: 'Technician assigned', color: '#64748B' }
};

/**
 * Display label/colour for an approvalHistory entry.
 * Maps legacy quote_sent_to_customer notes (WhatsApp / email / phone / in person) to specific titles.
 */
export function getApprovalHistoryDisplay(entry) {
  const status = entry?.status || '';
  const note = String(entry?.note || '').toLowerCase();

  if (status === 'quote_sent_whatsapp') return APPROVAL_HISTORY_EVENTS.quote_sent_whatsapp;
  if (status === 'quote_sent_email') return APPROVAL_HISTORY_EVENTS.quote_sent_email;
  if (status === 'quote_given_phone') return APPROVAL_HISTORY_EVENTS.quote_given_phone;
  if (status === 'quote_given_in_person') return APPROVAL_HISTORY_EVENTS.quote_given_in_person;

  if (status === 'quote_sent_to_customer') {
    if (note.includes('whatsapp')) return APPROVAL_HISTORY_EVENTS.quote_sent_whatsapp;
    if (note.includes('in person')) return APPROVAL_HISTORY_EVENTS.quote_given_in_person;
    if (note.includes('phone')) return APPROVAL_HISTORY_EVENTS.quote_given_phone;
    if (note.includes('email')) return APPROVAL_HISTORY_EVENTS.quote_sent_email;
    return APPROVAL_HISTORY_EVENTS.quote_sent_to_customer;
  }

  return (
    APPROVAL_HISTORY_EVENTS[status] ||
    APPROVAL_STATUSES[status] ||
    { label: status.replace(/_/g, ' ') || 'Unknown', color: '#6B7280' }
  );
}

export function normalizeQuoteStage(approvalStatus) {
  if (!approvalStatus || approvalStatus === 'pending_manager_approval') return 'in_review';
  if (approvalStatus === 'pending_customer_approval') return 'quote_sent_to_customer';
  if (APPROVAL_STATUSES[approvalStatus]) return approvalStatus;
  return 'in_review';
}

const QUOTE_DELIVERY_STATUSES = [
  'quote_sent_whatsapp',
  'quote_sent_email',
  'quote_given_phone',
  'quote_given_in_person',
  'quote_sent_to_customer'
];

/** Latest quote-delivery event for pills and cards (WhatsApp, email, phone, in person). */
export function getQuoteStageDisplay(job) {
  const ap = job?.approvalStatus;
  if (ap === 'job_authorised') return APPROVAL_STATUSES.job_authorised;
  const hist = [...(job?.approvalHistory || [])].sort((a, b) => new Date(b.at || 0) - new Date(a.at || 0));
  const lastQuote = hist.find((e) => QUOTE_DELIVERY_STATUSES.includes(e.status));
  if (lastQuote) return getApprovalHistoryDisplay(lastQuote);
  if (ap && APPROVAL_STATUSES[ap]) return APPROVAL_STATUSES[ap];
  return { label: 'Quote not set', color: '#94a3b8' };
}

/** Headline + detail for workshop job cards and modal workflow strip (no traffic / RAG). */
export function getWorkshopWorkflowCopy(job) {
  const pre = job.preWorkSubmissionStatus;
  const ap = job.approvalStatus || 'in_review';

  if (pre === 'rejected') {
    return {
      headline: 'Pre-work blocked',
      detail: 'Update inspection and resubmit'
    };
  }
  if (ap === 'job_authorised') {
    return {
      headline: 'Job authorised',
      detail: job.jobApprovalNote || 'Proceed with work on the vehicle'
    };
  }
  if (pre === 'pending_manager') {
    return {
      headline: 'Manager sign-off needed',
      detail: 'Pre-work inspection awaiting approval'
    };
  }
  if (pre === 'approved') {
    return {
      headline: 'Pre-work approved',
      detail: 'Continue quote / customer approval'
    };
  }
  if (['quote_sent_to_customer', 'pending_customer_approval'].includes(ap)) {
    return {
      headline: getQuoteStageDisplay(job).label,
      detail: 'Wait for customer go-ahead, then record job approval'
    };
  }
  return {
    headline: APPROVAL_STATUSES.in_review.label,
    detail: 'Review estimate and next steps'
  };
}

const mediaUrl = (item) => (typeof item === 'string' ? item : item?.url);

export function hasInspectionMedia(v) {
  if (!v) return false;
  if (v.inspectionVideoUrl) return true;
  const vb = v.videosBefore || v.videos || [];
  return Array.isArray(vb) && vb.length > 0 && vb.some((x) => !!mediaUrl(x));
}

export function hasCompletionMedia(v) {
  if (!v) return false;
  if (v.finalWorkVideoUrl) return true;
  const va = v.videosAfter || [];
  return Array.isArray(va) && va.length > 0 && va.some((x) => !!mediaUrl(x));
}

function hasTechnicianEstimate(job) {
  const est = job?.technicianEstimate;
  if (!est || typeof est !== 'object') return false;
  const amt = est.amount;
  const hasAmount = amt != null && amt !== '' && !Number.isNaN(Number(amt)) && Number(amt) >= 0;
  const hasDesc = typeof est.description === 'string' && est.description.trim().length > 0;
  return hasAmount || hasDesc;
}

/**
 * Linear job progress for workshop modal timeline (booked → inspection → quoted → decision → completed).
 * Each step: state 'complete' | 'current' | 'upcoming', optional decisionSub for the approval step.
 */
export function getJobProgressTimeline(job) {
  const pre = job?.preWorkSubmissionStatus;
  const ap = job?.approvalStatus || 'in_review';
  const hasInsp = hasInspectionMedia(job);
  const hasComp = hasCompletionMedia(job);
  const hasEst = hasTechnicianEstimate(job);

  const bookedComplete = !!(job?.dropOffDate || job?.receivedDate || job?.reg);
  const inspectionComplete =
    hasInsp || ['pending_manager', 'approved', 'rejected'].includes(pre) || !!job?.inspectionDone || hasEst;
  const quotedComplete = hasEst;
  const decisionApproved = ap === 'job_authorised';
  const decisionRejected = pre === 'rejected';
  const decisionComplete = decisionApproved || decisionRejected;
  const completedComplete = !!job?.collected || hasComp;

  /** Index of first step still in progress (0–4). If all done, points to last step. */
  let activeIndex = 0;
  if (!inspectionComplete) activeIndex = 1;
  else if (!quotedComplete) activeIndex = 2;
  else if (!decisionComplete) activeIndex = 3;
  else if (!completedComplete) activeIndex = 4;
  else activeIndex = 4;

  const allComplete =
    bookedComplete &&
    inspectionComplete &&
    quotedComplete &&
    decisionComplete &&
    completedComplete;

  const stepMeta = (index, completeFlag) => {
    if (allComplete || completeFlag) return 'complete';
    if (index === activeIndex) return 'current';
    return 'upcoming';
  };

  let decisionSub = '';
  if (decisionApproved) decisionSub = 'Approved';
  else if (decisionRejected) decisionSub = 'Rejected';
  else if (quotedComplete && !decisionComplete) decisionSub = 'Pending';

  const steps = [
    { id: 'booked', label: 'Booked in', state: stepMeta(0, bookedComplete) },
    { id: 'inspection', label: 'Inspection', state: stepMeta(1, inspectionComplete) },
    { id: 'quoted', label: 'Quoted', state: stepMeta(2, quotedComplete) },
    {
      id: 'decision',
      label: 'Approved / Rejected',
      state: stepMeta(3, decisionComplete),
      sublabel: decisionSub || undefined
    },
    { id: 'completed', label: 'Completed', state: stepMeta(4, completedComplete) }
  ];

  return { steps, activeIndex, allComplete };
}

/** Garage / workshop statuses that show quote workflow + extra job lines on cards */
export const GARAGE_WORKFLOW_STATUSES = new Set([
  'MOT',
  'Bodyshop',
  'Workshop',
  'Tyres',
  'In Progress',
  'Awaiting Parts'
]);
